/**
 * "Download my data": everything this device knows, as one JSON file (every group with its full
 * history, plus your cards), and every item as a CSV you can open in a spreadsheet.
 */
import { computeLedger, type StoredEvent } from "@split/core";
import { api } from "./api.ts";
import { app } from "./store.svelte.ts";
import { today } from "./format.ts";
import { APP_BUILD, APP_VERSION } from "./version.svelte.ts";

function save(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadJson(): Promise<void> {
  const groups: { id: string; name: string; events: StoredEvent[] }[] = [];
  for (const g of app.groups)
    groups.push({ id: g.id, name: g.name, events: await app.groupEvents(g.id) });
  let money: unknown = null;
  try {
    money = (await api<{ accounts: unknown[] }>("/api/admin/money")).accounts;
  } catch {
    /* offline or not the treasurer */
  }
  const file = {
    kind: "split-ledger-export",
    format: 1,
    exportedAt: new Date().toISOString(),
    appVersion: `${APP_VERSION} (${APP_BUILD})`,
    groups,
    money,
  };
  save(`split-ledger-${today()}.json`, JSON.stringify(file, null, 2), "application/json");
}

const cell = (v: unknown) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const dollars = (micro: number) => (micro / 1_000_000).toFixed(2);

export async function downloadCsv(): Promise<void> {
  const rows: string[][] = [
    [
      "Group",
      "Date",
      "Store",
      "Receipt",
      "Item #",
      "Description",
      "Category",
      "Price",
      "Coupon",
      "CRV",
      "Tax %",
      "Tip %",
      "Total",
      "Card",
      "Paid by",
      "Split between",
      "Shares",
      "Note",
    ],
  ];
  for (const g of app.groups) {
    const state = await app.groupSnapshot(g.id);
    if (!state) continue;
    const L = computeLedger(state, app.today);
    const name = (id: string) => state.people[id]?.name ?? "?";
    for (const c of L.items) {
      const it = c.item;
      rows.push([
        state.name || g.name,
        it.date,
        it.store,
        it.receipt ?? "",
        it.itemNo,
        it.name,
        c.category,
        (it.priceCents / 100).toFixed(2),
        (it.couponCents / 100).toFixed(2),
        (it.crvCents / 100).toFixed(2),
        (c.taxBp / 100).toString(),
        (c.tipBp / 100).toString(),
        dollars(c.totalMicro),
        it.paidWith ?? "",
        it.paidBy ? name(it.paidBy) : "",
        it.ticked.map(name).join("; "),
        Object.entries(c.shares)
          .map(([p, m]) => `${name(p)} ${dollars(m)}`)
          .join("; "),
        it.note,
      ]);
    }
  }
  save(
    `split-ledger-items-${today()}.csv`,
    rows.map((r) => r.map(cell).join(",")).join("\n"),
    "text/csv",
  );
}
